import {
  collection, doc, getDoc, getDocs, setDoc, onSnapshot, increment, runTransaction,
} from 'firebase/firestore';
import { db, auth } from '../firebase-config';

/**
 * Muuzbox Madness Mondays — the GUEST half of the game-night model.
 *
 * ⚠ PORT of muuzbox-reports-admin/src/services/gameNightService.js. That file
 * is the source of truth (the gamesmaster console writes the night); this is
 * the read + submit subset the legacy player layout needs. Host-only actions
 * (opening nights, starting rounds, calling bingo, marking answers) are
 * deliberately absent — guests never write those.
 *
 * THE THING THAT MUST NOT DRIFT: `gameNightId`. The console and every phone
 * derive the SAME document id from (channel name, 6AM-anchored date). Change
 * the slug rule or the 6AM anchor here and the guest silently watches a night
 * that doesn't exist — an empty games page with no error.
 */

// ── Night id — identical canon to headcountService/POS (6AM night key) ──
const slugify = (s) => (
  String(s || 'venue')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'venue'
);

const nightOfDate = (ms) => {
  const n = new Date(ms);
  if (n.getHours() < 6) n.setDate(n.getDate() - 1);
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const gameNightId = (channelName, atMs = Date.now()) =>
  `${slugify(channelName || 'venue')}__${nightOfDate(atMs)}`;

const nightRef = (nightId) => doc(db, 'gameNights', nightId);

// §3.1 points — keep in step with the console's copy.
export const POINTS = {
  join: 5,
  request: 2,
  mood: 1,
  correctAnswer: 3,
  whoRequestedIt: 5,
  wriTopTrack: 10,   // Who Requested It: your pick wins the room vote
  wriGuessWho: 10,   // Who Requested It: you guess who picked the winner
  bingoLine: 10,
  bingoTwoLines: 15,
  bingoFullHouse: 25,
  showdownWin: 20,
  attendance: 3,
};

/**
 * Lyric Challenge answer key — MUST match the admin service's copy byte for
 * byte (djb2 over `${raceStartMs}|${trackKey}`). Phones hash every tap and
 * compare against q.answerKey for instant right/wrong feedback.
 */
export const lyricAnswerKey = (raceStartMs, trackKeyStr) => {
  const s = `${raceStartMs}|${trackKeyStr}`;
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
};

// ── Reads ─────────────────────────────────────────────────────────────
export const subscribeNight = (nightId, cb) => {
  if (!nightId) return () => {};
  return onSnapshot(nightRef(nightId), (s) => cb(s.exists() ? { id: s.id, ...s.data() } : null),
    (e) => console.warn('[games] night listener:', e?.message));
};

/** Tonight's night for a channel, if the gamesmaster has opened one. */
export const subscribeTonight = (channelName, cb) => {
  // TWO candidate night docs, not one: the key embeds a 6AM-anchored LOCAL
  // date, so a guest in another timezone (or straddling the 6AM boundary)
  // computes a different key than the host who OPENED the night. Watching
  // today's and yesterday's keys and preferring the open one means every
  // viewer finds the venue's night regardless of their own clock. (Found
  // live: a Dubai guest saw no Join button for a UK venue's night.)
  const keyFor = (offsetDays) => gameNightId(channelName, Date.now() - offsetDays * 24 * 3600e3);
  const state = { today: undefined, yesterday: undefined };
  const emit = () => {
    // THE INVARIANT (2026-09-01 user spec): guests only ever see what the
    // gamesmaster's console sees. Today's key IS the console's definition of
    // "tonight", so it's trusted as-is. The yesterday key exists only for
    // clocks straddling the 6AM anchor (guest timezone ahead of the host, a
    // session running past midnight) and is trusted ONLY while the night can
    // PROVE it's genuinely in session — a round or night started within the
    // last 8 hours. An abandoned session can never pass that bar, so no
    // leftover round from any earlier game survives here, same day or not.
    // (Clock-window heuristics were tried twice and both leaked zombies.)
    const provablyInSession = (n) => {
      const last = Math.max(n?.liveRound?.startedAtMs || 0, n?.startedAtMs || 0);
      return last > Date.now() - 8 * 3600e3;
    };
    const pick = (state.today && state.today.status !== 'closed') ? state.today
      : (state.yesterday && state.yesterday.status !== 'closed' && provablyInSession(state.yesterday)) ? state.yesterday
      : state.today ? state.today
      : (state.yesterday && state.yesterday.status === 'closed') ? state.yesterday
      : null;
    cb(pick);
  };
  const unsubs = [
    onSnapshot(doc(db, 'gameNights', keyFor(0)),
      (s) => { state.today = s.exists() ? { nightId: s.id, ...s.data() } : null; emit(); },
      () => { state.today = null; emit(); }),
    onSnapshot(doc(db, 'gameNights', keyFor(1)),
      (s) => { state.yesterday = s.exists() ? { nightId: s.id, ...s.data() } : null; emit(); },
      () => { state.yesterday = null; emit(); }),
  ];
  return () => unsubs.forEach((u) => { try { u(); } catch (_) {} });
};

export const subscribePlayers = (nightId, cb) => {
  if (!nightId) return () => {};
  return onSnapshot(collection(db, 'gameNights', nightId, 'players'), (snap) => {
    const rows = [];
    snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
    rows.sort((a, b) => (b.points || 0) - (a.points || 0));
    cb(rows);
  }, (e) => console.warn('[games] players listener:', e?.message));
};

// ── Writes a guest is allowed to make ─────────────────────────────────
/**
 * Award points. A caller-supplied `ledgerId` makes it IDEMPOTENT — the mood
 * self-award re-fires on refresh and must never pay twice.
 */
export const awardPoints = async (nightId, { uid, teamId, displayName, action, points, roundId = null, ledgerId = null }) => {
  if (!nightId || !uid || !points) return;
  const p = Number(points) || 0;
  const id = ledgerId || `pt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  const ledgerDoc = {
    uid, teamId: teamId || null, action, points: p, roundId, atMs: Date.now(),
    byUid: auth.currentUser?.uid || null,
  };
  const playerPatch = {
    uid, displayName: displayName || '', points: increment(p), lastScoredAtMs: Date.now(),
  };
  if (ledgerId) {
    // ATOMIC, not read-then-write: the old existence check raced. After a
    // popup sign-in both the doJoin continuation AND the auto-join effect
    // are alive in the same page; both read "no ledger entry yet", both
    // wrote the SAME ledger doc (one row survives) and both ran the player
    // increment — 6 points on a single 3-point ledger row (launcher,
    // 2026-08-30). Inside a transaction the loser sees the winner's doc
    // and pays nothing.
    let paid = false;
    try {
      paid = await runTransaction(db, async (tx) => {
        const ref = doc(db, 'gameNights', nightId, 'ledger', id);
        const snap = await tx.get(ref);
        if (snap.exists()) return false;
        tx.set(ref, ledgerDoc);
        tx.set(doc(db, 'gameNights', nightId, 'players', uid), playerPatch, { merge: true });
        if (teamId) tx.set(doc(db, 'gameNights', nightId, 'teams', teamId), { points: increment(p) }, { merge: true });
        return true;
      });
    } catch (e) {
      console.warn('[games] award transaction failed:', e?.message);
      return { skipped: true };
    }
    if (!paid) return { skipped: true };
  } else {
    await setDoc(doc(db, 'gameNights', nightId, 'ledger', id), ledgerDoc);
    await setDoc(doc(db, 'gameNights', nightId, 'players', uid), playerPatch, { merge: true });
    if (teamId) {
      await setDoc(doc(db, 'gameNights', nightId, 'teams', teamId), { points: increment(p) }, { merge: true });
    }
  }
  // Only reached when the award actually paid.
  try {
    const nSnap = await getDoc(nightRef(nightId));
    const seasonId = nSnap.exists() ? nSnap.data().seasonId : null;
    if (seasonId) {
      await setDoc(doc(db, 'gameSeasons', seasonId, 'standings', uid), {
        uid, displayName: displayName || '', points: increment(p), lastNightId: nightId,
      }, { merge: true });
    }
  } catch (_) {}
  return { scored: true };
};

/** Join tonight. Idempotent; pays the join bonus ONCE ever plus attendance. */
export const joinNight = async (nightId, { uid, displayName, photoURL, teamId = null, tableId = null }) => {
  if (!nightId || !uid) return { joined: false };
  // THE GOOGLE-ONLY DOOR (2026-08-28 user spec): an anonymous Firebase
  // session must NEVER reach a game roster. The games panel sends anonymous /
  // unsigned guests to Google sign-in before joining; this guard is the
  // backstop so NO call site can put an anonymous uid on a roster.
  const session = auth.currentUser;
  if (!session || session.isAnonymous || session.uid !== uid) {
    return { joined: false, reason: 'anonymous' };
  }
  const pRef = doc(db, 'gameNights', nightId, 'players', uid);
  const existing = await getDoc(pRef);
  if (existing.exists()) return { joined: true, already: true };

  await setDoc(pRef, {
    uid, displayName: displayName || 'Guest', photoURL: photoURL || null,
    teamId, tableId: tableId || null, points: 0, joinedAtMs: Date.now(),
  }, { merge: true });

  let firstEver = false;
  try {
    const nSnap = await getDoc(nightRef(nightId));
    const seasonId = nSnap.exists() ? nSnap.data().seasonId : null;
    if (seasonId) {
      const st = await getDoc(doc(db, 'gameSeasons', seasonId, 'standings', uid));
      firstEver = !st.exists();
    }
  } catch (_) {}

  // Deterministic ledgerIds make BOTH join awards idempotent per night —
  // without them, two concurrent joinNight calls (a manual Join tap racing
  // the post-sign-in auto-join, or two live surfaces completing the same
  // join) each passed the roster-existence check and paid attendance twice
  // (seen live on bb, 2026-08-30: one guest started on 6 instead of 3).
  await awardPoints(nightId, {
    uid, teamId, displayName, action: 'attendance', points: POINTS.attendance,
    ledgerId: `attendance_${uid}`,
  });
  if (firstEver) {
    await awardPoints(nightId, {
      uid, teamId, displayName, action: 'join', points: POINTS.join,
      ledgerId: `join_${uid}`,
    });
  }
  return { joined: true, firstEver };
};

/** A joined guest picks (or moves) table mid-night. */
export const setPlayerTable = (nightId, uid, tableId) =>
  setDoc(doc(db, 'gameNights', nightId, 'players', uid), { tableId: tableId || null }, { merge: true });

/**
 * An answer / bingo claim. One entry per player per round — or per QUESTION
 * when qIndex is given (Name That Tune, Lyric Sudden Death).
 */
export const submitEntry = async (nightId, roundId, { uid, teamId, tableId, displayName, answer, kind = 'answer', qIndex = null, trackId = null, choiceIndex = null, coverart = null }) => {
  if (!nightId || !roundId || !uid) return;
  const entryId = qIndex === null || qIndex === undefined ? uid : `${uid}_q${qIndex}`;
  await setDoc(doc(db, 'gameNights', nightId, 'rounds', roundId, 'entries', entryId), {
    uid, teamId: teamId || null, tableId: tableId || null,
    displayName: displayName || '', answer: answer ?? null, trackId: trackId || null,
    choiceIndex: choiceIndex ?? null, coverart: coverart || null,
    kind, qIndex: qIndex ?? null, atMs: Date.now(), correct: null, points: 0,
  }, { merge: true });
};

/**
 * The playlist the DESKTOP has loaded — the search space for Name That Tune.
 * Same two locations the admin dashboard reads.
 */
export const getLoadedPlaylistTracks = async (venueUid) => {
  const none = { playlistId: null, playlistName: null, tracks: [] };
  if (!venueUid) return none;
  let playlistId = null;
  let playlistName = null;
  for (const col of ['users', 'clients']) {
    try {
      const snap = await getDoc(doc(db, col, venueUid));
      const cp = snap.exists() ? snap.data().currentPlaylist : null;
      if (cp) {
        playlistId = typeof cp === 'string' ? cp : cp.id || null;
        playlistName = typeof cp === 'object' ? cp.name || null : null;
      }
    } catch (_) {}
    if (playlistId) break;
  }
  if (!playlistId) return none;

  let tracksSnap = null;
  try {
    tracksSnap = await getDocs(collection(db, 'users', venueUid, 'playlists', playlistId, 'tracks'));
  } catch (_) {}
  if (!tracksSnap || tracksSnap.empty) {
    try {
      tracksSnap = await getDocs(collection(db, 'playlists', playlistId, 'tracks'));
    } catch (_) {}
  }
  const tracks = [];
  if (tracksSnap) {
    tracksSnap.forEach((d) => {
      const t = d.data() || {};
      tracks.push({
        id: d.id,
        title: t.track || t.title || 'Unknown',
        artist: t.artist || '',
        coverart: t.coverart || '',
      });
    });
  }
  return { playlistId, playlistName, tracks };
};


// users/{uid}.photoURL — the ACTUAL avatar (Google photo, or the identicon
// every guest account gets). Request docs carry uid but no photo, so the
// line-up resolves each distinct requester's profile once and caches it.
const _photoCache = new Map();
const _profilePhoto = async (uid) => {
  if (!uid) return null;
  if (_photoCache.has(uid)) return _photoCache.get(uid);
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    const p = (snap.exists() && (snap.data().photoURL || snap.data().photoUrl)) || null;
    _photoCache.set(uid, p);
    return p;
  } catch (_) {
    return null;
  }
};

/**
 * Who Requested It — the LINE-UP, derived on the phone itself.
 *
 * Live-deduped list of everyone who has made a request tonight (name +
 * avatar). Deliberately guest-side: the game must not depend on the console
 * publishing anything, so the list is there the moment the round opens.
 * Alphabetical — a stable order that cannot leak who requested last.
 */
export const watchNightRequesters = (venueUid, sinceMs, cb) => {
  if (!venueUid) return () => {};
  return onSnapshot(collection(db, 'users', venueUid, 'djchats'), (snap) => {
    const seen = new Map();
    snap.forEach((d) => {
      const x = d.data() || {};
      const at = (() => {
        const t = x.timestamp || x.createdAt || x.time;
        if (!t) return 0;
        if (typeof t?.toMillis === 'function') return t.toMillis();
        if (typeof t?.seconds === 'number') return t.seconds * 1000;
        return Number(t) || Date.parse(t) || 0;
      })();
      if (at < (sinceMs || 0)) return;
      const name = String(x.user || '').trim();
      if (!name || seen.has(name.toLowerCase())) return;
      seen.set(name.toLowerCase(), {
        name,
        uid: x.uid || null,
        photo: x.photoURL || x.photo || null,
      });
    });
    const list = Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
    cb(list);   // names first — faces fill in as profiles resolve
    Promise.all(list.map(async (c) => (
      c.photo ? c : { ...c, photo: await _profilePhoto(c.uid) }
    ))).then((withPhotos) => {
      if (withPhotos.some((c, i) => c.photo !== list[i].photo)) cb(withPhotos);
    });
  }, (e) => console.warn('[games] requester watch:', e?.message));
};
